// AI Orchestration — Live Dashboard (super-admin + supervisor).
import { useEffect, useState, useCallback } from "react";
import { Loader2, Bot, Users, Clock, CheckCircle2, ArrowUpRight, Inbox, TrendingUp, RefreshCw, Star, AlertTriangle, BookOpen } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { LineChartC, BarChartC } from "@/admin/pages/reports/_charts";
import OrchConversations from "@/components/OrchConversations";

const REASON_TREND_LABEL = {
  human_request: "Human req", negative_sentiment: "Neg sentiment",
  low_kb_confidence: "Low KB", max_bot_turns: "Max turns",
  draft_review: "Draft", no_bot: "No bot", bot_error: "Bot error", unknown: "Other",
};

const STATE_META = {
  bot_handling: { label: "Bot Handling", icon: Bot, tone: "sky" },
  pending_assignment: { label: "Pending Claim", icon: Inbox, tone: "amber" },
  agent_assigned: { label: "Agent Assigned", icon: Users, tone: "blue" },
  bot_resolved: { label: "Bot Resolved", icon: CheckCircle2, tone: "green" },
  closed: { label: "Closed", icon: CheckCircle2, tone: "gray" },
};
const TONE = {
  sky: "bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-900 text-sky-700 dark:text-sky-300",
  amber: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-300",
  blue: "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900 text-blue-700 dark:text-blue-300",
  green: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-300",
  gray: "bg-muted/40 border-border text-muted-foreground",
};
const REASON_LABEL = {
  human_request: "Human requested", negative_sentiment: "Negative sentiment",
  low_kb_confidence: "Bot couldn't answer", max_bot_turns: "Max bot turns",
  draft_review: "Email draft review", no_bot: "Bot off", bot_error: "Bot error",
  bot_unavailable: "Bot offline (LLM)", send_failed: "Auto-send failed",
};

function fmtWait(s) {
  s = Math.max(0, Math.round(s || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

export default function OrchestrationDashboard() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [agents, setAgents] = useState([]);
  const [trend, setTrend] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const [d, s, a, t, g] = await Promise.all([
        api.get("/orchestrator/dashboard"),
        api.get("/orchestrator/reports/summary?days=7"),
        api.get("/orchestrator/reports/agents?days=7"),
        api.get("/orchestrator/reports/trend?days=14"),
        api.get("/orchestrator/reports/kb-gaps?days=14"),
      ]);
      setData(d.data); setSummary(s.data); setAgents(a.data.agents || []); setTrend(t.data); setGaps(g.data.gaps || []);
    } catch (e) { if (!silent) toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 5000);
    return () => clearInterval(t);
  }, [load]);

  if (loading || !data) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

  const enabled = data.config?.enabled;
  const byState = data.by_state || {};
  const today = data.today || {};

  return (
    <div className="px-4 sm:px-6 py-6 max-w-6xl" data-testid="orch-dashboard">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <Bot className="w-6 h-6 text-primary" /> AI Orchestration — Live
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time bot triage, escalation queue and agent load.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${enabled ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-muted text-muted-foreground"}`} data-testid="orch-dash-status">
            {enabled ? "● Orchestration ON" : "○ Orchestration OFF"}
          </span>
          <button onClick={() => load()} className="text-muted-foreground hover:text-foreground" data-testid="orch-refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Skill coverage alert */}
      {data.uncovered_count > 0 && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-950/30 px-4 py-3" data-testid="orch-coverage-alert">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-bold text-red-700 dark:text-red-300">{data.uncovered_count} pending chat{data.uncovered_count > 1 ? "s" : ""} have no available agent with the required skill</div>
            <div className="text-red-600/90 dark:text-red-400/90 text-xs mt-0.5">
              Missing coverage for: {(data.uncovered_skills || []).join(", ") || "—"}. Set an agent Available or assign these skills. ({data.available_agents} agent{data.available_agents === 1 ? "" : "s"} available)
            </div>
          </div>
        </div>
      )}

      {/* State tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {Object.keys(STATE_META).map((k) => {
          const m = STATE_META[k]; const Icon = m.icon;
          return (
            <Card key={k} className={`p-4 border ${TONE[m.tone]}`} data-testid={`orch-tile-${k}`}>
              <Icon className="w-5 h-5 mb-2 opacity-80" />
              <div className="text-2xl font-bold tabular-nums">{byState[k] || 0}</div>
              <div className="text-xs font-medium mt-0.5">{m.label}</div>
            </Card>
          );
        })}
      </div>

      {/* Today KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Card className="p-4" data-testid="orch-kpi-botreplies"><div className="text-xs text-muted-foreground flex items-center gap-1"><Bot className="w-3.5 h-3.5" /> Bot replies today</div><div className="text-2xl font-bold mt-1">{today.bot_replies || 0}</div></Card>
        <Card className="p-4" data-testid="orch-kpi-escalations"><div className="text-xs text-muted-foreground flex items-center gap-1"><ArrowUpRight className="w-3.5 h-3.5" /> Escalations today</div><div className="text-2xl font-bold mt-1">{today.escalations || 0}</div></Card>
        <Card className="p-4" data-testid="orch-kpi-resolved"><div className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Resolved today</div><div className="text-2xl font-bold mt-1">{today.resolved || 0}</div></Card>
        <Card className="p-4" data-testid="orch-kpi-deflection"><div className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Deflection (7d)</div><div className="text-2xl font-bold mt-1">{summary?.deflection_pct ?? 0}%</div></Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Pending queue */}
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between bg-muted/30">
            <div className="font-display font-bold flex items-center gap-2"><Inbox className="w-4 h-4 text-amber-500" /> Pending Claim Queue</div>
            <span className="text-xs text-muted-foreground">{data.pending_count} waiting · longest {fmtWait(data.longest_wait_seconds)}</span>
          </div>
          <div className="max-h-80 overflow-y-auto" data-testid="orch-pending-list">
            {(data.pending || []).length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">No conversations waiting 🎉</div>
            ) : (data.pending || []).map((c) => (
              <div key={c.id} className={`px-4 py-3 border-b last:border-0 flex items-center justify-between gap-3 ${c.covered === false ? "bg-red-50/60 dark:bg-red-950/20" : ""}`} data-testid={`orch-pending-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-1.5">
                    {c.contact_name || "Unknown"}
                    {c.covered === false && <span className="text-[10px] font-bold text-red-600 dark:text-red-400 flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> no cover</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    <span className="capitalize">{c.primary_channel}</span> · {REASON_LABEL[c.escalation_reason] || c.escalation_reason}
                    {c.bot_draft ? " · ✍️ draft ready" : ""}
                    {c.preferred_agent ? <span className="text-primary"> · <Star className="w-3 h-3 inline fill-current" /> best: {c.preferred_agent.name}</span> : ""}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1 justify-end"><Clock className="w-3 h-3" /> {fmtWait(c.wait_seconds)}</div>
                  {(c.required_skills || []).length > 0 && <div className="text-[10px] text-muted-foreground mt-0.5">{c.required_skills.join(", ")}</div>}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Agent load */}
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b bg-muted/30 font-display font-bold flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" /> Agent Load (active)</div>
          <div className="max-h-80 overflow-y-auto" data-testid="orch-agent-load">
            {(data.agent_load || []).length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">No active assignments</div>
            ) : (data.agent_load || []).map((a) => (
              <div key={a.agent_id} className="px-4 py-3 border-b last:border-0 flex items-center justify-between" data-testid={`orch-load-${a.agent_id}`}>
                <span className="font-medium">{a.name || a.agent_id}</span>
                <span className="text-sm bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 px-2.5 py-0.5 rounded-full font-medium">{a.active} active</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Escalation reasons */}
        <Card className="p-4">
          <div className="font-display font-bold mb-3 flex items-center gap-2"><ArrowUpRight className="w-4 h-4 text-primary" /> Escalation Reasons (7d)</div>
          {Object.keys(summary?.escalation_reasons || {}).length === 0 ? (
            <p className="text-sm text-muted-foreground">No escalations in range.</p>
          ) : Object.entries(summary.escalation_reasons).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between py-1.5 text-sm border-b last:border-0" data-testid={`orch-reason-${k}`}>
              <span>{REASON_LABEL[k] || k}</span><span className="font-bold">{v}</span>
            </div>
          ))}
        </Card>

        {/* Agent performance */}
        <Card className="p-4">
          <div className="font-display font-bold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> Agent Performance (7d)</div>
          {agents.length === 0 ? <p className="text-sm text-muted-foreground">No claimed conversations in range.</p> : (
            <table className="w-full text-sm" data-testid="orch-agent-report">
              <thead className="text-xs text-muted-foreground"><tr><th className="text-left py-1">Agent</th><th className="text-right py-1">Claimed</th><th className="text-right py-1">Closed</th><th className="text-right py-1">Avg claim</th></tr></thead>
              <tbody>
                {agents.map((a) => (
                  <tr key={a.agent_id} className="border-t"><td className="py-1.5">{a.name}</td><td className="text-right">{a.claimed}</td><td className="text-right">{a.closed}</td><td className="text-right">{fmtWait(a.avg_time_to_claim_seconds)}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* Live Conversations debug panel */}
      <OrchConversations />

      {/* Bot knowledge gaps */}
      {gaps.length > 0 && (
        <Card className="p-4 mt-5" data-testid="orch-kb-gaps">
          <div className="flex items-center justify-between mb-1">
            <div className="font-display font-bold flex items-center gap-2"><BookOpen className="w-4 h-4 text-primary" /> Bot Knowledge Gaps (14d)</div>
            <a href="/admin/kb" className="text-xs text-primary hover:underline font-medium">Add to Knowledge Base →</a>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Questions the bot escalated because it couldn't answer confidently — add these to the KB to raise deflection.</p>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {gaps.map((g, i) => (
              <div key={i} className="flex items-start justify-between gap-3 py-1.5 border-b last:border-0" data-testid={`orch-gap-${i}`}>
                <div className="min-w-0">
                  <div className="text-sm truncate">{g.question}</div>
                  <div className="text-[11px] text-muted-foreground">{(g.channels || []).join(", ")}</div>
                </div>
                <span className="shrink-0 text-xs font-bold bg-muted px-2 py-0.5 rounded-full">×{g.count}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Trend charts (last 14 days) */}
      {trend && trend.series && trend.series.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-5 mt-5" data-testid="orch-trends">
          <Card className="p-4">
            <div className="font-display font-bold mb-1 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> Deflection & Time-to-Claim (14d)</div>
            <p className="text-xs text-muted-foreground mb-3">Bot deflection % and average agent time-to-claim (seconds) per day.</p>
            <LineChartC data={trend.series} xKey="day" yKeys={[
              { id: "deflection_pct", label: "Deflection %" },
              { id: "avg_time_to_claim", label: "Avg claim (s)" },
            ]} height={240} />
          </Card>
          <Card className="p-4">
            <div className="font-display font-bold mb-1 flex items-center gap-2"><ArrowUpRight className="w-4 h-4 text-primary" /> Escalations by Reason (14d)</div>
            <p className="text-xs text-muted-foreground mb-3">Daily escalations broken down by trigger.</p>
            <BarChartC data={trend.series} xKey="day" stacked height={240}
              yKeys={(trend.reasons || []).map((r) => ({ id: `r_${r}`, label: REASON_TREND_LABEL[r] || r }))} />
          </Card>
        </div>
      )}
    </div>
  );
}
